function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
// ----------------------------------------------------------------------------
// Helper: Recharge Curated Bundle
// ----------------------------------------------------------------------------
// Shared logic for adding "curated" subscription products (Recharge dynamic
// bundles) to the cart. A curated product is a bundle parent whose contents
// are preset via metafields (custom.bundle_collections +
// custom.preset_month_selection) rather than chosen by the shopper.
//
// Used by:
// - snippets/product-form (PDP add to cart)
// - sections/subscription-cards (direct Add to Cart mode)
//
// The Recharge SDK is loaded lazily on first use so pages without curated
// products never pay for it.
// ----------------------------------------------------------------------------

var RECHARGE_SDK_URL = 'https://static.rechargecdn.com/assets/storefront/recharge-client-1.70.0.min.js';
var rechargeSdkLoadPromise = null;
var normalizeId = value => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};
var parseQuantity = function parseQuantity(value) {
  var defaultValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
  var parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : defaultValue;
};
var getRechargeBundleApi = () => {
  var _window, _window2;
  if ((_window = window) !== null && _window !== void 0 && (_window = _window.recharge) !== null && _window !== void 0 && _window.bundle) return window.recharge.bundle;
  if ((_window2 = window) !== null && _window2 !== void 0 && (_window2 = _window2.ReCharge) !== null && _window2 !== void 0 && _window2.bundle) return window.ReCharge.bundle;
  return null;
};
var initializeRechargeSdk = () => {
  var rechargeGlobal = window.recharge || window.ReCharge;
  if (!rechargeGlobal || typeof rechargeGlobal.init !== 'function') return;
  try {
    var _window3;
    rechargeGlobal.init({
      storeIdentifier: (_window3 = window) === null || _window3 === void 0 || (_window3 = _window3.Shopify) === null || _window3 === void 0 ? void 0 : _window3.shop,
      appName: 'sitka-seafood-market',
      appVersion: '1.0.0'
    });
  } catch (error) {
    // Ignore init failures (e.g. SDK already initialized).
  }
};
var loadRechargeSdkScript = () => {
  if (getRechargeBundleApi()) return Promise.resolve();
  if (rechargeSdkLoadPromise) return rechargeSdkLoadPromise;
  rechargeSdkLoadPromise = new Promise((resolve, reject) => {
    var existingScript = document.querySelector('script[data-recharge-sdk-loader="curated-bundle"]');
    if (existingScript) {
      if (existingScript.dataset.loaded === 'true' || existingScript.readyState === 'loaded' || existingScript.readyState === 'complete') {
        resolve();
        return;
      }
      existingScript.addEventListener('load', () => resolve(), {
        once: true
      });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Recharge SDK script.')), {
        once: true
      });
      return;
    }
    var script = document.createElement('script');
    script.src = RECHARGE_SDK_URL;
    script.async = true;
    script.dataset.rechargeSdkLoader = 'curated-bundle';
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => reject(new Error('Failed to load Recharge SDK script.'));
    document.head.appendChild(script);
  });
  return rechargeSdkLoadPromise;
};
export var ensureRechargeBundleApi = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator(function* () {
    var bundleApi = getRechargeBundleApi();
    if (bundleApi) return bundleApi;
    yield loadRechargeSdkScript();
    initializeRechargeSdk();
    bundleApi = getRechargeBundleApi();
    if (bundleApi) return bundleApi;
    throw new Error('Recharge bundle SDK is not available on this page.');
  });
  return function ensureRechargeBundleApi() {
    return _ref.apply(this, arguments);
  };
}();

/**
 * Build the `/cart/add.js` items for a curated bundle product.
 *
 * @param {Object} options
 * @param {string|number} options.parentProductId  Bundle parent product id
 * @param {string|number} options.parentVariantId  Bundle parent variant id
 * @param {string|number} [options.sellingPlanId]  Selling plan shared by parent and selection
 * @param {string|number} options.collectionId     Recharge bundle collection id
 * @param {string|number} options.presetProductId  Preset month selection product id
 * @param {string|number} options.presetVariantId  Preset month selection variant id
 * @param {string} options.productHandle           Bundle parent product handle
 * @param {number} [options.quantity]              Selection quantity (defaults to 1)
 * @returns {Promise<Array>} Cart line items ready for `cart.add({ items })`
 */
export var buildCuratedBundleItems = /*#__PURE__*/function () {
  var _ref3 = _asyncToGenerator(function* (_ref2) {
    var parentProductId = _ref2.parentProductId,
      parentVariantId = _ref2.parentVariantId,
      sellingPlanId = _ref2.sellingPlanId,
      collectionId = _ref2.collectionId,
      presetProductId = _ref2.presetProductId,
      presetVariantId = _ref2.presetVariantId,
      productHandle = _ref2.productHandle,
      _ref2$quantity = _ref2.quantity,
      quantity = _ref2$quantity === void 0 ? 1 : _ref2$quantity;
    var bundleApi = yield ensureRechargeBundleApi();
    if (typeof bundleApi.getDynamicBundleItems !== 'function') {
      throw new Error('Recharge bundle methods are unavailable in this SDK version.');
    }
    var externalProductId = normalizeId(parentProductId);
    var externalVariantId = normalizeId(parentVariantId);
    var normalizedCollectionId = normalizeId(collectionId);
    var normalizedPresetProductId = normalizeId(presetProductId);
    var normalizedPresetVariantId = normalizeId(presetVariantId);
    var normalizedSellingPlanId = normalizeId(sellingPlanId);
    if (!externalProductId || !externalVariantId) {
      throw new Error('Unable to build curated bundle payload: missing parent product/variant data.');
    }
    if (!normalizedCollectionId || !normalizedPresetProductId || !normalizedPresetVariantId) {
      throw new Error('Unable to build curated bundle payload: missing preset month selection data.');
    }
    var selection = {
      collectionId: normalizedCollectionId,
      externalProductId: normalizedPresetProductId,
      externalVariantId: normalizedPresetVariantId,
      quantity: parseQuantity(quantity, 1)
    };
    if (normalizedSellingPlanId) {
      selection.sellingPlan = normalizedSellingPlanId;
    }
    var bundleProductData = {
      externalProductId,
      externalVariantId,
      selections: [selection]
    };
    var cartItems = yield Promise.resolve(bundleApi.getDynamicBundleItems(bundleProductData, productHandle));
    if (!Array.isArray(cartItems) || cartItems.length === 0) {
      throw new Error('Recharge did not return bundle cart items.');
    }
    return cartItems;
  });
  return function buildCuratedBundleItems(_x) {
    return _ref3.apply(this, arguments);
  };
}();